import React from "react";
import { useTranslation } from "react-i18next";
import { floorplannerStore } from "../../../../store/floorplannerStore";
import {
  CircleStairsType,
  RectStairsType,
  StairShape,
  StairTurnDirection,
  TurnStairsType,
} from "../../../../types/floorplannerTypes";
import {
  circleStairsOpenAngleForStepCount,
  getCircularStairStepCount,
  getRectStairStepCount,
  getStairEffectiveFloorDistance,
  rectStairsHeightForStepCount,
  stairBaseElevationFromEffectiveFloor,
} from "../../../../features/floorplan3d/symbols/primitives/stairs/stairGeometry";
import DimensionField from "../components/DimensionField";
import ObjectRotationField from "../components/ObjectRotationField";
import RotationField from "../components/RotationField";
import MetricsSection from "../components/MetricsSection";
import SubsectionTitle from "../components/SubsectionTitle";
import View3DControlsGrid from "../components/View3DControlsGrid";
import View3DSection from "../components/View3DSection";
import ToggleRow from "../components/ToggleRow";
import UnitHeader from "../components/UnitHeader";
import type { PropertyPanelController } from "../types";
import {
  areNumbersEqual,
  degreesToRadians,
  getCommonDegreesValue,
  getCommonMillimeterValue,
  getCommonValue,
  panelGridFieldClassName,
  panelObjectTitleRowClassName,
  radiansToRoundedDegrees,
  toDisplayLength,
  toWorldFromDisplay,
} from "../propertyPanelUtils";

type StairsSectionProps = Pick<PropertyPanelController, "updateGlobalState"> & {
  selectedCircleStairs: CircleStairsType[];
  displayedCircleStairs: CircleStairsType[];
  selectedRectStairs: RectStairsType[];
  displayedRectStairs: RectStairsType[];
  selectedTurnStairs: TurnStairsType[];
  displayedTurnStairs: TurnStairsType[];
  is3DView: boolean;
};

type StairsSymbol = CircleStairsType | RectStairsType | TurnStairsType;

/** Matches geometry clamp and the flight-step input min/max. */
const MIN_FLIGHT_STEPS = 1;
const MAX_FLIGHT_STEPS = 128;

/**
 * Parse a flight-step input value. Returns null for empty/non-numeric so we
 * never persist NaN into symbol state.
 */
const parseFlightStepsInput = (raw: string): number | null => {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.min(MAX_FLIGHT_STEPS, Math.max(MIN_FLIGHT_STEPS, parsed));
};

const getStair3DEffectiveFloorDistance = (stair: StairsSymbol) =>
  getStairEffectiveFloorDistance(stair, {
    defaultStepSize: floorplannerStore.stairStepSize,
  });
const getStair3DPitchDegrees = (stair: StairsSymbol) =>
  radiansToRoundedDegrees(stair.threeD?.rotation3D?.pitch ?? 0);
const getStair3DRollDegrees = (stair: StairsSymbol) =>
  radiansToRoundedDegrees(stair.threeD?.rotation3D?.roll ?? 0);
const getStair3DPlanRotationDegrees = (stair: StairsSymbol) =>
  radiansToRoundedDegrees(stair.rotation ?? 0);

const Stair3DControls = <T extends StairsSymbol>({
  selected,
  stair,
  updateGlobalState,
}: {
  selected: T[];
  stair: T;
  updateGlobalState: PropertyPanelController["updateGlobalState"];
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  return (
    <View3DSection>
      <div className="flex flex-col gap-1.5 py-0.5">
        <View3DControlsGrid>
          <DimensionField
            label={t("propertyPanel.distanceFromFloor")}
            fieldClassName={panelGridFieldClassName}
            value={
              selected.length > 1
                ? (getCommonMillimeterValue(
                    selected,
                    getStair3DEffectiveFloorDistance,
                  ) ?? "")
                : toDisplayLength(
                    getStair3DEffectiveFloorDistance(stair),
                  )
            }
            placeholder={selected.length > 1 ? mixed : undefined}
            onChange={(nextValue) => {
              const effectiveWorld = toWorldFromDisplay(
                parseFloat(nextValue),
              );
              selected.forEach((s) => {
                const rawBaseElev = stairBaseElevationFromEffectiveFloor(
                  s,
                  effectiveWorld,
                  {
                    defaultStepSize: floorplannerStore.stairStepSize,
                  },
                );
                updateGlobalState(
                  [s],
                  "symbol3DBaseElevation",
                  toDisplayLength(rawBaseElev),
                );
              });
            }}
          />
          <ObjectRotationField
            label={t("propertyPanel.rotateObject")}
            value={
              selected.length > 1
                ? (getCommonValue(
                    selected,
                    getStair3DPlanRotationDegrees,
                    areNumbersEqual,
                  ) ?? "")
                : getStair3DPlanRotationDegrees(stair)
            }
            placeholder={selected.length > 1 ? mixed : undefined}
            onChange={(nextValue) => {
              const degrees = parseFloat(nextValue);
              updateGlobalState(
                selected,
                "rotation",
                degreesToRadians(Number.isFinite(degrees) ? degrees : 0),
              );
            }}
          />
          <ObjectRotationField
            label={t("propertyPanel.turnObject")}
            value={
              selected.length > 1
                ? (getCommonValue(
                    selected,
                    getStair3DPitchDegrees,
                    areNumbersEqual,
                  ) ?? "")
                : getStair3DPitchDegrees(stair)
            }
            placeholder={selected.length > 1 ? mixed : undefined}
            onChange={(nextValue) =>
              updateGlobalState(selected, "shape3DPitch", nextValue)
            }
          />
          <ObjectRotationField
            label={t("propertyPanel.tiltObject")}
            value={
              selected.length > 1
                ? (getCommonValue(
                    selected,
                    getStair3DRollDegrees,
                    areNumbersEqual,
                  ) ?? "")
                : getStair3DRollDegrees(stair)
            }
            placeholder={selected.length > 1 ? mixed : undefined}
            onChange={(nextValue) =>
              updateGlobalState(selected, "shape3DRoll", nextValue)
            }
          />
        </View3DControlsGrid>
        <ToggleRow
          label={t("propertyPanel.reverseRiseDirection")}
          checked={
            getCommonValue(
              selected,
              (symbol) => symbol.flipVertical || false,
            ) ?? false
          }
          onChange={(checked) =>
            updateGlobalState(selected, "flipVertical", checked)
          }
        />
      </div>
    </View3DSection>
  );
};

const StairsBlock = <T extends CircleStairsType | RectStairsType>({
  title,
  pluralTitle,
  selected,
  displayed,
  widthProperty,
  getWidth,
  heightProperty,
  getHeight,
  updateGlobalState,
  is3DView,
}: {
  title: string;
  pluralTitle: string;
  selected: T[];
  displayed: T[];
  widthProperty: string;
  getWidth: (stair: T) => number | undefined;
  /** When set with getHeight, show linked width/length (rectangular stairs). */
  heightProperty?: string;
  getHeight?: (stair: T) => number | undefined;
  updateGlobalState: PropertyPanelController["updateGlobalState"];
  is3DView: boolean;
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");
  const hasIndependentLength = Boolean(heightProperty && getHeight);
  const stairGeometryOptions = {
    defaultStepSize: floorplannerStore.stairStepSize,
  };
  const getStepCount = (symbol: T) =>
    hasIndependentLength
      ? getRectStairStepCount(
          symbol as RectStairsType,
          stairGeometryOptions,
        )
      : getCircularStairStepCount(
          symbol as CircleStairsType,
          stairGeometryOptions,
        );

  return (
    <>
      {displayed.map((stair) => (
        <div key={`${widthProperty}-${stair?.id}`}>
          <div className="border-b border-gray-300" />
          <div className="text-sm font-normal">
            <div className={panelObjectTitleRowClassName}>
              <UnitHeader
                title={
                  selected.length > 1
                    ? `${selected.length} ${pluralTitle}`
                    : title
                }
              />
            </div>
            <MetricsSection>
              {hasIndependentLength && heightProperty && getHeight ? (
                <div className="grid grid-cols-2 items-end gap-2 py-2 pb-2">
                  <DimensionField
                    label={t("propertyPanel.width")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selected.length > 1
                        ? (getCommonMillimeterValue(selected, getWidth) ?? "")
                        : toDisplayLength(getWidth(stair) || 0)
                    }
                    placeholder={selected.length > 1 ? mixed : undefined}
                    onChange={(nextValue) => {
                      const value = parseFloat(nextValue);
                      if (!Number.isFinite(value)) return;
                      updateGlobalState(selected, widthProperty, value);
                    }}
                  />
                  <DimensionField
                    label={t("propertyPanel.length")}
                    fieldClassName={panelGridFieldClassName}
                    value={
                      selected.length > 1
                        ? (getCommonMillimeterValue(selected, getHeight) ?? "")
                        : toDisplayLength(getHeight(stair) || 0)
                    }
                    placeholder={selected.length > 1 ? mixed : undefined}
                    onChange={(nextValue) => {
                      const value = parseFloat(nextValue);
                      if (!Number.isFinite(value)) return;
                      updateGlobalState(selected, heightProperty, value);
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-between py-2 pb-2">
                  <DimensionField
                    label={t("propertyPanel.width")}
                    value={
                      selected.length > 1
                        ? (getCommonMillimeterValue(selected, getWidth) ?? "")
                        : toDisplayLength(getWidth(stair) || 0)
                    }
                    placeholder={selected.length > 1 ? mixed : undefined}
                    onChange={(nextValue) =>
                      updateGlobalState(
                        selected,
                        widthProperty,
                        parseFloat(nextValue),
                      )
                    }
                  />
                </div>
              )}
            </MetricsSection>
            <SubsectionTitle>
              {t("propertyPanel.stairConfiguration")}
            </SubsectionTitle>
            <div className="p-3 flex flex-col gap-2 py-2 pb-4">
              <div className="flex flex-row justify-between items-center">
                <label htmlFor={`steps-${stair.id}`}>
                  {t("propertyPanel.steps")}
                </label>
                <input
                  id={`steps-${stair.id}`}
                  type="number"
                  min={1}
                  max={128}
                  className="border border-gray-300 rounded p-1 w-[90px]"
                  value={
                    selected.length > 1
                      ? (getCommonValue(
                          selected,
                          getStepCount,
                          areNumbersEqual,
                        ) ?? "")
                      : getStepCount(stair)
                  }
                  placeholder={selected.length > 1 ? mixed : undefined}
                  onChange={(e) => {
                    const steps = parseInt(e.target.value, 10);
                    if (!Number.isFinite(steps)) return;
                    /**
                     * Growing/shrinking via step count mirrors turn-stair
                     * Flight 1/2 controls: length (rect) or sweep (circle)
                     * becomes steps × tread depth.
                     */
                    selected.forEach((symbol) => {
                      const tread =
                        symbol.stairStepSize ||
                        floorplannerStore.stairStepSize;
                      if (hasIndependentLength && heightProperty) {
                        updateGlobalState(
                          [symbol],
                          heightProperty,
                          toDisplayLength(
                            rectStairsHeightForStepCount(
                              steps,
                              tread,
                              floorplannerStore.stairStepSize,
                            ),
                          ),
                        );
                        return;
                      }
                      updateGlobalState(
                        [symbol],
                        "openAngle",
                        circleStairsOpenAngleForStepCount(
                          steps,
                          getWidth(symbol),
                          tread,
                          floorplannerStore.stairStepSize,
                        ),
                      );
                    });
                  }}
                />
              </div>
              <DimensionField
                label={t("propertyPanel.stepDepth")}
                value={
                  selected.length > 1
                    ? (getCommonMillimeterValue(
                        selected,
                        (symbol) =>
                          symbol.stairStepSize ||
                          floorplannerStore.stairStepSize,
                      ) ?? "")
                    : toDisplayLength(
                        stair?.stairStepSize || floorplannerStore.stairStepSize,
                      )
                }
                placeholder={selected.length > 1 ? mixed : undefined}
                onChange={(nextValue) =>
                  updateGlobalState(
                    selected,
                    "stairStepSize",
                    parseFloat(nextValue),
                  )
                }
              />
            </div>
            {!is3DView && <div className="border-b border-gray-300" />}
            {is3DView && (
              <Stair3DControls
                selected={selected}
                stair={stair}
                updateGlobalState={updateGlobalState}
              />
            )}
            {!is3DView && (
              <div className="p-3 flex items-center justify-between py-2">
                <RotationField
                  value={
                    selected.length > 1
                      ? (getCommonDegreesValue(selected) ?? "")
                      : radiansToRoundedDegrees(stair?.rotation || 0)
                  }
                  placeholder={selected.length > 1 ? mixed : undefined}
                  onChange={(degrees) =>
                    updateGlobalState(
                      selected,
                      "rotation",
                      degreesToRadians(degrees),
                    )
                  }
                />
              </div>
            )}
          </div>
          <div className="border-b border-gray-300" />
        </div>
      ))}
    </>
  );
};

/**
 * Property editors for L/U turn stairs: shape, direction, flights, landing, and shared 3D transforms.
 */
const TurnStairsBlock = ({
  selected,
  displayed,
  updateGlobalState,
  is3DView,
}: {
  selected: TurnStairsType[];
  displayed: TurnStairsType[];
  updateGlobalState: PropertyPanelController["updateGlobalState"];
  is3DView: boolean;
}) => {
  const { t } = useTranslation();
  const mixed = t("common.mixed");

  return (
    <>
      {displayed.map((stair) => (
        <div key={`turnStairs-${stair.id}`}>
          <div className="border-b border-gray-300" />
          <div className="text-sm font-normal">
            <div className={panelObjectTitleRowClassName}>
              <UnitHeader
                title={
                  selected.length > 1
                    ? `${selected.length} ${t("propertyPanel.turnStaircases")}`
                    : t("propertyPanel.turnStaircase")
                }
              />
            </div>
            <div className="border-b border-gray-300" />
            <SubsectionTitle>
              {t("propertyPanel.stairConfiguration")}
            </SubsectionTitle>
            <div className="p-3 flex flex-col gap-2 py-2 pb-4">
              <div className="flex flex-row justify-between items-center">
                <div>{t("propertyPanel.stairShape")}</div>
                <select
                  className="border border-gray-300 rounded p-1 w-[120px]"
                  aria-label={t("propertyPanel.stairShape")}
                  value={
                    selected.length > 1
                      ? (getCommonValue(selected, (s) => s.stairShape) ?? "")
                      : stair.stairShape
                  }
                  onChange={(e) =>
                    updateGlobalState(
                      selected,
                      "stairShape",
                      e.target.value as StairShape,
                    )
                  }
                >
                  {getCommonValue(selected, (s) => s.stairShape) ===
                    undefined &&
                    selected.length > 1 && (
                      <option value="">{mixed}</option>
                    )}
                  <option value="L">{t("propertyPanel.stairShapeL")}</option>
                  <option value="U">{t("propertyPanel.stairShapeU")}</option>
                </select>
              </div>
              <div className="flex flex-row justify-between items-center">
                <div>{t("propertyPanel.turnDirection")}</div>
                <select
                  className="border border-gray-300 rounded p-1 w-[120px]"
                  aria-label={t("propertyPanel.turnDirection")}
                  value={
                    selected.length > 1
                      ? (getCommonValue(selected, (s) => s.turnDirection) ??
                        "")
                      : stair.turnDirection
                  }
                  onChange={(e) =>
                    updateGlobalState(
                      selected,
                      "turnDirection",
                      e.target.value as StairTurnDirection,
                    )
                  }
                >
                  {getCommonValue(selected, (s) => s.turnDirection) ===
                    undefined &&
                    selected.length > 1 && (
                      <option value="">{mixed}</option>
                    )}
                  <option value="left">
                    {t("propertyPanel.turnDirectionLeft")}
                  </option>
                  <option value="right">
                    {t("propertyPanel.turnDirectionRight")}
                  </option>
                </select>
              </div>
              <div className="flex flex-row justify-between items-center">
                <label htmlFor={`flight1-${stair.id}`}>
                  {t("propertyPanel.firstFlightSteps")}
                </label>
                <input
                  id={`flight1-${stair.id}`}
                  type="number"
                  min={MIN_FLIGHT_STEPS}
                  max={MAX_FLIGHT_STEPS}
                  className="border border-gray-300 rounded p-1 w-[90px]"
                  value={
                    selected.length > 1
                      ? (getCommonValue(
                          selected,
                          (s) => s.firstFlightSteps,
                          areNumbersEqual,
                        ) ?? "")
                      : stair.firstFlightSteps
                  }
                  placeholder={selected.length > 1 ? mixed : undefined}
                  onChange={(e) => {
                    const nextSteps = parseFlightStepsInput(e.target.value);
                    if (nextSteps === null) {
                      return;
                    }
                    updateGlobalState(selected, "firstFlightSteps", nextSteps);
                  }}
                />
              </div>
              <div className="flex flex-row justify-between items-center">
                <label htmlFor={`flight2-${stair.id}`}>
                  {t("propertyPanel.secondFlightSteps")}
                </label>
                <input
                  id={`flight2-${stair.id}`}
                  type="number"
                  min={MIN_FLIGHT_STEPS}
                  max={MAX_FLIGHT_STEPS}
                  className="border border-gray-300 rounded p-1 w-[90px]"
                  value={
                    selected.length > 1
                      ? (getCommonValue(
                          selected,
                          (s) => s.secondFlightSteps,
                          areNumbersEqual,
                        ) ?? "")
                      : stair.secondFlightSteps
                  }
                  placeholder={selected.length > 1 ? mixed : undefined}
                  onChange={(e) => {
                    const nextSteps = parseFlightStepsInput(e.target.value);
                    if (nextSteps === null) {
                      return;
                    }
                    updateGlobalState(selected, "secondFlightSteps", nextSteps);
                  }}
                />
              </div>
            </div>
            <MetricsSection>
              <div className="flex flex-col gap-2 py-2 pb-2">
              <DimensionField
                label={t("propertyPanel.flightWidth")}
                value={
                  selected.length > 1
                    ? (getCommonMillimeterValue(
                        selected,
                        (s) => s.stairWidth,
                      ) ?? "")
                    : toDisplayLength(stair.stairWidth || 0)
                }
                placeholder={selected.length > 1 ? mixed : undefined}
                onChange={(nextValue) =>
                  updateGlobalState(
                    selected,
                    "stairWidth",
                    parseFloat(nextValue),
                  )
                }
              />
              {/* U-shape only: L landings are sized from flight width automatically. */}
              {(selected.length > 1
                ? getCommonValue(selected, (s) => s.stairShape)
                : stair.stairShape) === "U" && (
                <DimensionField
                  label={t("propertyPanel.landingDepth")}
                  value={
                    selected.length > 1
                      ? (getCommonMillimeterValue(
                          selected,
                          (s) => s.landingDepth,
                        ) ?? "")
                      : toDisplayLength(stair.landingDepth || 0)
                  }
                  placeholder={selected.length > 1 ? mixed : undefined}
                  onChange={(nextValue) =>
                    updateGlobalState(
                      selected,
                      "landingDepth",
                      parseFloat(nextValue),
                    )
                  }
                />
              )}
              <DimensionField
                label={t("propertyPanel.stepDepth")}
                value={
                  selected.length > 1
                    ? (getCommonMillimeterValue(
                        selected,
                        (symbol) =>
                          symbol.stairStepSize ||
                          floorplannerStore.stairStepSize,
                      ) ?? "")
                    : toDisplayLength(
                        stair.stairStepSize || floorplannerStore.stairStepSize,
                      )
                }
                placeholder={selected.length > 1 ? mixed : undefined}
                onChange={(nextValue) =>
                  updateGlobalState(
                    selected,
                    "stairStepSize",
                    parseFloat(nextValue),
                  )
                }
              />
              </div>
            </MetricsSection>
            {is3DView && (
              <Stair3DControls
                selected={selected}
                stair={stair}
                updateGlobalState={updateGlobalState}
              />
            )}
            {!is3DView && (
              <div className="p-3 flex items-center justify-between py-2">
                <RotationField
                  value={
                    selected.length > 1
                      ? (getCommonDegreesValue(selected) ?? "")
                      : radiansToRoundedDegrees(stair.rotation || 0)
                  }
                  placeholder={selected.length > 1 ? mixed : undefined}
                  onChange={(degrees) =>
                    updateGlobalState(
                      selected,
                      "rotation",
                      degreesToRadians(degrees),
                    )
                  }
                />
              </div>
            )}
          </div>
          <div className="border-b border-gray-300" />
        </div>
      ))}
    </>
  );
};

const StairsSection: React.FC<StairsSectionProps> = ({
  selectedCircleStairs,
  displayedCircleStairs,
  selectedRectStairs,
  displayedRectStairs,
  selectedTurnStairs,
  displayedTurnStairs,
  updateGlobalState,
  is3DView,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <StairsBlock
        title={t("propertyPanel.circleStaircase")}
        pluralTitle={t("propertyPanel.circularStaircases")}
        selected={selectedCircleStairs}
        displayed={displayedCircleStairs}
        widthProperty="circleStairsWidth"
        getWidth={(stair) => stair.circleStairsWidth}
        updateGlobalState={updateGlobalState}
        is3DView={is3DView}
      />
      <StairsBlock
        title={t("propertyPanel.rectangularStaircase")}
        pluralTitle={t("propertyPanel.rectangularStaircases")}
        selected={selectedRectStairs}
        displayed={displayedRectStairs}
        widthProperty="rectStairsWidth"
        getWidth={(stair) => stair.rectStairsWidth}
        heightProperty="rectStairsHeight"
        getHeight={(stair) => stair.rectStairsHeight}
        updateGlobalState={updateGlobalState}
        is3DView={is3DView}
      />
      <TurnStairsBlock
        selected={selectedTurnStairs}
        displayed={displayedTurnStairs}
        updateGlobalState={updateGlobalState}
        is3DView={is3DView}
      />
    </>
  );
};

export default StairsSection;
